import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import { documentManagementApi, DocumentTypeItem } from '../api/documentManagementApi';
import { ExpiryNotificationSettings } from '../api/documentManagementApi';

const DocumentManagementSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Expiry Notification Settings state
  const [expirySettings, setExpirySettings] = useState<ExpiryNotificationSettings>({
    notificationBeforeDays: 30,
    emailNotification: true,
    smsNotification: false,
    systemNotification: true,
    isActive: true,
  });
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [expirySaving, setExpirySaving] = useState(false);

  // Load Expiry Notification Settings
  const loadExpirySettings = React.useCallback(async () => {
    setExpiryLoading(true);
    try {
      const data = await documentManagementApi.getExpiryNotificationSettings();
      setExpirySettings(data);
    } catch {
      toast.error('Failed to load expiry notification settings');
    } finally {
      setExpiryLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 1) {
      void loadExpirySettings();
    }
  }, [activeTab, loadExpirySettings]);

  // Handle Save Expiry Notification Settings
  const handleSaveExpirySettings = async () => {
    setExpirySaving(true);
    try {
      await documentManagementApi.saveExpiryNotificationSettings(expirySettings);
      toast.success('Expiry notification settings saved');
    } catch {
      toast.error('Failed to save expiry notification settings');
    } finally {
      setExpirySaving(false);
    }
  };
  const [documentTypeName, setDocumentTypeName] = useState('');
  const [description, setDescription] = useState('');
  const [expiryRequired, setExpiryRequired] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isDateOfIssueApplicable, setIsDateOfIssueApplicable] = useState<'yes' | 'no'>('no');
  const [isPlaceOfIssueApplicable, setIsPlaceOfIssueApplicable] = useState<'yes' | 'no'>('no');
  const [isExpiryDateApplicable, setIsExpiryDateApplicable] = useState<'yes' | 'no'>('no');
  const [isAttachmentRequired, setIsAttachmentRequired] = useState<'yes' | 'no'>('yes');
  const [whoCanView, setWhoCanView] = useState('Only Admin');
  const [whoCanEdit, setWhoCanEdit] = useState('Only Admin');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadDocumentTypes = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentManagementApi.getDocumentTypes();
      setDocumentTypes(data);
    } catch {
      toast.error('Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 0) {
      void loadDocumentTypes();
    }
  }, [activeTab, loadDocumentTypes]);

  const resetForm = () => {
    setDocumentTypeName('');
    setDescription('');
    setExpiryRequired(false);
    setIsActive(true);
    setIsDateOfIssueApplicable('no');
    setIsPlaceOfIssueApplicable('no');
    setIsExpiryDateApplicable('no');
    setIsAttachmentRequired('yes');
    setWhoCanView('Only Admin');
    setWhoCanEdit('Only Admin');
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const handleSaveDocumentType = async () => {
    const trimmedName = documentTypeName.trim();

    if (!trimmedName) {
      toast.warning('Document Type Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        documentTypeName: trimmedName,
        description: description.trim() || undefined,
        expiryRequired: isExpiryDateApplicable === 'yes',
        isActive,
      };

      if (editingId) {
        await documentManagementApi.updateDocumentType(editingId, payload);
        toast.success('Document type updated successfully');
      } else {
        await documentManagementApi.createDocumentType(payload);
        toast.success('Document type added successfully');
      }

      resetForm();
      setIsModalOpen(false);
      await loadDocumentTypes();
    } catch {
      toast.error(editingId ? 'Failed to update document type' : 'Failed to add document type');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: DocumentTypeItem) => {
    setEditingId(item.id);
    setDocumentTypeName(item.documentTypeName);
    setDescription(item.description ?? '');
    setExpiryRequired(item.expiryRequired);
    setIsExpiryDateApplicable(item.expiryRequired ? 'yes' : 'no');
    setIsDateOfIssueApplicable('no');
    setIsPlaceOfIssueApplicable('no');
    setIsAttachmentRequired('yes');
    setWhoCanView('Only Admin');
    setWhoCanEdit('Only Admin');
    setIsActive(item.isActive);
    setIsModalOpen(true);
  };

  const renderYesNoField = (
    label: string,
    value: 'yes' | 'no',
    onChange: (value: 'yes' | 'no') => void
  ) => (
    <FormControl>
      <FormLabel sx={{ color: 'text.primary', mb: 1 }}>{label}</FormLabel>
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value as 'yes' | 'no')}>
        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
      </RadioGroup>
    </FormControl>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Document Management Settings
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue: number) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="Document Types" sx={{ textTransform: "none" }}/>
          <Tab label="Expiry Notification Settings" sx={{ textTransform: "none" }}/>
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, mt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Document Types</Typography>
            <Button variant="contained" onClick={openAddModal} sx={{ textTransform: 'none' }}>
              Add Document
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Total Records: {documentTypes.length}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Document Type Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Expiry Required</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : documentTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No document found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    documentTypes.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.documentTypeName}</TableCell>
                        <TableCell>{item.description || '-'}</TableCell>
                        <TableCell>{item.expiryRequired ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{item.isActive ? 'Active' : 'Inactive'}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Dialog
            open={isModalOpen}
            onClose={closeModal}
            maxWidth="md"
            fullWidth
            PaperProps={{ 'data-app-dialog-skip-top-close': 'true' }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={700}>
                  {editingId ? 'Update DocumentType' : 'Add DocumentType'}
                </Typography>
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Document Type Name *:
                  </Typography>
                  <TextField
                    fullWidth
                    value={documentTypeName}
                    onChange={(e) => setDocumentTypeName(e.target.value)}
                    placeholder="e.g. Passport"
                    inputProps={{ maxLength: 100 }}
                    helperText={`${documentTypeName.length}/100 characters`}
                  />
                </Box>

                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Document Type Description:
                  </Typography>
                  <TextField
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description about this document type"
                    inputProps={{ maxLength: 200 }}
                    helperText={`${description.length}/200 characters`}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                  }}
                >
                  <Box>
                    {renderYesNoField('Is Date of Issue applicable?', isDateOfIssueApplicable, setIsDateOfIssueApplicable)}
                  </Box>
                  <Box>
                    {renderYesNoField('Is Place of Issue applicable?', isPlaceOfIssueApplicable, setIsPlaceOfIssueApplicable)}
                  </Box>
                  <Box>
                    {renderYesNoField('Is Expiry Date applicable?', isExpiryDateApplicable, setIsExpiryDateApplicable)}
                  </Box>
                  <Box>
                    {renderYesNoField('Is Attachment required?', isAttachmentRequired, setIsAttachmentRequired)}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" fontSize="1.05rem" mb={1.5}>
                    Access settings:
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label={isActive ? 'Active' : 'Inactive'}
                    />
                  </Box>

                  <Stack spacing={1.5}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 0.5 }}>Who can view this document?</FormLabel>
                      <Select value={whoCanView} onChange={(e) => setWhoCanView(e.target.value)}>
                        <MenuItem value="Only Admin">Only Admin</MenuItem>
                        <MenuItem value="HR Admin">HR Admin</MenuItem>
                        <MenuItem value="Organization Admin">Organization Admin</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 0.5 }}>Who can add/edit this document?</FormLabel>
                      <Select value={whoCanEdit} onChange={(e) => setWhoCanEdit(e.target.value)}>
                        <MenuItem value="Only Admin">Only Admin</MenuItem>
                        <MenuItem value="HR Admin">HR Admin</MenuItem>
                        <MenuItem value="Organization Admin">Organization Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>

                <Box textAlign="center" pt={1}>
                  <Button
                    variant="contained"
                    onClick={handleSaveDocumentType}
                    disabled={saving || !documentTypeName.trim()}
                    sx={{textTransform:'none'}}
                  >
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </Button>
                </Box>
              </Stack>
            </DialogContent>
          </Dialog>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 2, mt: 2 }}>
          <Typography variant="h6" mb={2}>
            Expiry Notification Settings
          </Typography>

          <Stack spacing={2}>
            <TextField
              type="number"
              label="Notification Before Days"
              fullWidth
              placeholder="30"
              inputProps={{ min: 1 }}
              value={expirySettings.notificationBeforeDays}
              onChange={e => setExpirySettings(s => ({ ...s, notificationBeforeDays: Number(e.target.value) }))}
              disabled={expiryLoading || expirySaving}
            />
            <FormControlLabel
              control={<Switch checked={expirySettings.systemNotification} onChange={e => setExpirySettings(s => ({ ...s, systemNotification: e.target.checked }))} disabled={expiryLoading || expirySaving} />}
              label="System Notification"
            />
            <FormControlLabel
              control={<Switch checked={expirySettings.isActive} onChange={e => setExpirySettings(s => ({ ...s, isActive: e.target.checked }))} disabled={expiryLoading || expirySaving} />}
              label="Active"
            />
            <Box>
              <Button variant="contained" onClick={handleSaveExpirySettings} disabled={expirySaving || expiryLoading} sx={{ textTransform: 'none' }}>
                {expirySaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Add Document now opens a modal with the same field structure as requested.
        </Typography>
      </Paper> */}
    </Box>
  );
};

export default DocumentManagementSettingsPage;
